<template>
  <div class="p-6 space-y-6 min-h-full">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">Query History</h1>
        <p class="text-gray-500 dark:text-gray-400">Monitor system queries, performance metrics, and audit logs</p>
      </div>
      <button 
        @click="exportLogs"
        class="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-xl font-medium shadow-lg shadow-purple-500/25 transition-all duration-300 flex items-center gap-2"
      >
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export Logs
      </button>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <!-- Total Queries Card -->
      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Queries</div>
          <div class="p-2 bg-blue-500/10 rounded-lg">
            <svg class="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div class="text-3xl font-bold text-gray-900 dark:text-white mb-2">{{ stats.totalQueries }}</div>
        <div class="flex items-center text-xs text-emerald-500">
             <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
             +12.5% vs last week
        </div>
      </div>

      <!-- Avg Latency Card -->
      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Avg Latency</div>
          <div class="p-2 bg-purple-500/10 rounded-lg">
             <svg class="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
             </svg>
          </div>
        </div>
        <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">{{ stats.avgLatency }}</h3>
        <p class="flex items-center text-xs text-emerald-500">
           <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
           -8.2% vs last week
        </p>
      </div>

      <!-- Error Rate Card -->
      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Error Rate</div>
          <div class="p-2 bg-rose-500/10 rounded-lg">
             <svg class="w-6 h-6 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
             </svg>
          </div>
        </div>
        <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">{{ stats.errorRate }}</h3>
        <p class="flex items-center text-xs text-emerald-500">
           <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
           -0.01% vs last week
        </p>
      </div>

      <!-- Active Sessions Card -->
      <div class="glass-panel p-6">
        <div class="flex justify-between items-start mb-2">
          <div class="text-gray-500 dark:text-gray-400 text-sm font-medium">Active Sessions</div>
          <div class="p-2 bg-amber-500/10 rounded-lg">
             <svg class="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
             </svg>
          </div>
        </div>
        <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">{{ stats.activeSessions }}</h3>
        <p class="flex items-center text-xs text-emerald-500">
           <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
           +3.2% vs last week
        </p>
      </div>
    </div>

    <!-- Main Content Panel -->
    <div class="glass-panel p-6">
      <!-- Search and Filters -->
      <div class="flex flex-col md:flex-row justify-between gap-4 mb-8">
        <div class="flex-1 max-w-lg relative">
          <svg class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search queries, user IDs, or error codes..." 
            class="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
          />
        </div>
        <div class="flex gap-3 overflow-x-auto pb-1 md:pb-0">
          <select v-model="statusFilter" class="px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50 cursor-pointer min-w-[140px]">
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
             <option value="timeout">Timeout</option>
          </select>
          <select v-model="timeFilter" class="px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50 cursor-pointer min-w-[140px]">
             <option value="24h">Last 24 Hours</option>
             <option value="7d">Last 7 Days</option>
             <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      <!-- Queries Table -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left border-b border-gray-200 dark:border-white/10">
              <th class="pb-4 pl-4 font-semibold text-gray-600 dark:text-gray-400">Query ID</th>
              <th class="pb-4 font-semibold text-gray-600 dark:text-gray-400">User</th>
              <th class="pb-4 font-semibold text-gray-600 dark:text-gray-400">Query Text</th>
              <th class="pb-4 font-semibold text-gray-600 dark:text-gray-400">Duration</th>
              <th class="pb-4 font-semibold text-gray-600 dark:text-gray-400">Status</th>
              <th class="pb-4 font-semibold text-gray-600 dark:text-gray-400">Timestamp</th>
              <th class="pb-4 pr-4 font-semibold text-gray-600 dark:text-gray-400 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-white/5">
            <tr v-for="query in filteredQueries" :key="query.id" class="group hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
              <td class="py-4 pl-4">
                <span class="font-mono text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-white/10 px-2 py-1 rounded">{{ query.id }}</span>
              </td>
              <td class="py-4">
                 <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-400 to-indigo-500 flex items-center justify-center text-xs text-white font-bold">
                       {{ query.userInitials }}
                    </div>
                    <div>
                       <p class="text-sm font-medium text-gray-900 dark:text-white">{{ query.userName }}</p>
                       <p class="text-xs text-gray-500 dark:text-gray-400">{{ query.userRole }}</p>
                    </div>
                 </div>
              </td>
              <td class="py-4 max-w-xs truncate">
                <span class="text-sm text-gray-600 dark:text-gray-300 font-mono">{{ query.text }}</span>
              </td>
              <td class="py-4">
                 <span class="text-sm text-gray-600 dark:text-gray-300">{{ query.duration }}ms</span>
              </td>
              <td class="py-4">
                <span :class="getStatusClasses(query.status)" class="px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize flex items-center w-fit gap-1.5">
                   <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDotColor(query.status)"></span>
                   {{ query.status }}
                </span>
              </td>
              <td class="py-4">
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ query.timestamp }}</span>
              </td>
              <td class="py-4 pr-4 text-right">
                <button 
                  @click="viewQuery(query)"
                  class="p-2 text-gray-400 hover:text-purple-500 transition-colors"
                  title="View Details"
                >
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

       <!-- Pagination -->
      <div class="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-white/10 pt-6">
        <p class="text-sm text-gray-500 dark:text-gray-400">Showing <span class="font-semibold text-gray-900 dark:text-white">1</span> to <span class="font-semibold text-gray-900 dark:text-white">8</span> of <span class="font-semibold text-gray-900 dark:text-white">12,408</span> results</p>
        <div class="flex gap-2">
          <button class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          <button class="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg shadow-lg shadow-purple-500/30 hover:bg-purple-500 transition-all">
            1
          </button>
          <button class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 transition-colors">
            2
          </button>
          <button class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 transition-colors">
            3
          </button>
          <span class="px-2 py-2 text-gray-500">...</span>
          <button class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 transition-colors">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

// Stats Data
const stats = ref({
  totalQueries: '1.2M',
  avgLatency: '45ms',
  errorRate: '0.04%',
  activeSessions: '842'
})

// Sample Data
const queries = [
  { id: 'qry_8x92jn', userName: 'Sarah Johnson', userInitials: 'SJ', userRole: 'Data Scientist', text: 'SELECT * FROM conversions WHERE campaign_id = "cp_2910"...', duration: 124, status: 'success', timestamp: '2 mins ago' },
  { id: 'qry_7k21la', userName: 'Michael Chen', userInitials: 'MC', userRole: 'Engineer', text: 'UPDATE user_settings SET preference = "dark" WHERE...', duration: 45, status: 'success', timestamp: '5 mins ago' },
  { id: 'qry_9p03mk', userName: 'Emily Rodriguez', userInitials: 'ER', userRole: 'Admin', text: 'DELETE FROM logs WHERE created_at < "2023-01-01"', duration: 8900, status: 'timeout', timestamp: '12 mins ago' },
  { id: 'qry_2m55op', userName: 'David Kim', userInitials: 'DK', userRole: 'Analyst', text: 'SELECT COUNT(*) as dau FROM analytics_events GROUP BY...', duration: 2310, status: 'success', timestamp: '15 mins ago' },
  { id: 'qry_4j77qr', userName: 'Jessica Martinez', userInitials: 'JM', userRole: 'Viewer', text: 'FETCH user_profile WHERE id = 882910', duration: 32, status: 'failed', timestamp: '22 mins ago' },
  { id: 'qry_1b33st', userName: 'Robert Taylor', userInitials: 'RT', userRole: 'Engineer', text: 'INSERT INTO audit_logs VALUES (...)', duration: 67, status: 'success', timestamp: '30 mins ago' },
  { id: 'qry_6v11uv', userName: 'Amanda White', userInitials: 'AW', userRole: 'Admin', text: 'SELECT * FROM users LIMIT 100', duration: 15, status: 'success', timestamp: '45 mins ago' },
  { id: 'qry_5c88wx', userName: 'Christopher Lee', userInitials: 'CL', userRole: 'Data Scientist', text: 'ANALYZE TABLE subscription_metrics', duration: 5430, status: 'processing', timestamp: '1 hour ago' },
]

// State
const searchQuery = ref('')
const statusFilter = ref('')
const timeFilter = ref('24h')

// Computed
const filteredQueries = computed(() => {
  return queries.filter(q => {
    const matchesSearch = q.text.toLowerCase().includes(searchQuery.value.toLowerCase()) || 
                          q.userName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                          q.id.toLowerCase().includes(searchQuery.value.toLowerCase())
    const matchesStatus = statusFilter.value ? q.status === statusFilter.value : true
    return matchesSearch && matchesStatus
  })
})

// Helpers
const getStatusClasses = (status: string) => {
  switch (status) {
    case 'success': return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20'
    case 'failed': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20'
    case 'timeout': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20'
    case 'processing': return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20'
    default: return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300'
  }
}

const getStatusDotColor = (status: string) => {
   switch (status) {
    case 'success': return 'bg-emerald-500'
    case 'failed': return 'bg-rose-500'
    case 'timeout': return 'bg-amber-500'
    case 'processing': return 'bg-blue-500 animate-pulse'
    default: return 'bg-gray-500'
  }
}

// Actions
const exportLogs = () => {
  alert('Exporting query logs to CSV...')
}

const viewQuery = (query: any) => {
  alert(`Viewing details for query ID: ${query.id}`)
}
</script>
